/**
 * 阿里云日志服务 SLS WebTracking
 *
 * 通过 HTTP GET 请求直接上报日志到阿里云 SLS
 * 参考：https://help.aliyun.com/zh/sls/use-the-web-tracking-feature-to-collect-logs
 *
 * Usage:
 *   slsLogger.setUserId(userId)
 *   slsLogger.trackPage('/home', pvKey, { name: 'John' })
 *   slsLogger.trackAction('click', 'button', { label: '提交' })
 *   slsLogger.trackError(new Error('something went wrong'))
 */

import { SLS_CONFIG } from "./slsConfig"
import { getDeviceId } from "./deviceFingerprint"
import type { LogEvent } from "./types"

const ServiceURL = `https://${SLS_CONFIG.PROJECT}.${SLS_CONFIG.ENDPOINT}/logstores/${SLS_CONFIG.LOGSTORE}/track`
const APIVersion = "0.6.0"

let userId = ""
const requestQueue: string[] = []
let resendStarted = false

// 页面跳转追踪
let previousPath = ""
let currentPath = ""
let currentPvKey = ""
let hasTrackedLeave = false

/** 生成页面访问唯一标识 */
export const generatePvKey = (): string =>
  `${Date.now()}_${Math.random().toString(36).substring(2, 11)}`

/** 获取 referrer */
const getReferrer = (): string => {
  if (document.referrer) {
    return document.referrer
  }
  if (previousPath && previousPath !== window.location.pathname) {
    return `${window.location.origin}${previousPath}`
  }
  return ""
}

/** 解析 pvKey */
const resolvePvKey = (
  explicitPvKey?: string,
  data?: Record<string, unknown>,
): string => {
  const dataPvKey = data?.pvKey as string | undefined
  return explicitPvKey ?? dataPvKey ?? currentPvKey
}

/** 构建日志参数 */
const buildLogParams = (
  event: LogEvent,
): { logParams: Record<string, string>; logRequestURL: string } => {
  const deviceid = getDeviceId()
  const referrer = getReferrer()
  const dataWithPvKey = {
    ...event.data,
    pvKey: event.data?.pvKey ?? event.pvKey,
  }
  const logParams: Record<string, string> = {
    ...event,
    APIVersion,
    userid: userId,
    deviceid,
    data: JSON.stringify({
      ...dataWithPvKey,
      timestamp: Date.now(),
      url: window.location.href,
      referrer,
    }),
  }

  const logRequestURL = `${ServiceURL}?${new URLSearchParams(logParams)}`
  return { logParams, logRequestURL }
}

/** 发送事件日志（异步） */
const sendEventLog = async (event: LogEvent) => {
  const { logParams, logRequestURL } = buildLogParams(event)

  if (process.env.NODE_ENV !== "production") {
    console.log("[SLSLogger] Log ignored (non-production mode).", logParams)
    return
  }

  console.log(`[Sending]${logRequestURL}`)
  try {
    await fetch(logRequestURL, {
      method: "GET",
      mode: "cors",
      credentials: "omit",
    })
  } catch {
    console.log(`[failed]${logRequestURL}`)
    requestQueue.push(logRequestURL)
    resendFailedLogs()
  }
}

/** 发送事件日志（同步，用于页面卸载） */
const sendEventLogSync = (event: LogEvent) => {
  const { logParams, logRequestURL } = buildLogParams(event)

  if (process.env.NODE_ENV !== "production") {
    console.log(
      "[SLSLogger] Sync log ignored (non-production mode).",
      logParams,
    )
    return
  }

  try {
    if (navigator.sendBeacon) {
      const success = navigator.sendBeacon(logRequestURL)
      if (!success) {
        fetch(logRequestURL, {
          method: "GET",
          mode: "cors",
          credentials: "omit",
          keepalive: true,
        }).catch(() => {})
      }
    } else {
      fetch(logRequestURL, {
        method: "GET",
        mode: "cors",
        credentials: "omit",
        keepalive: true,
      }).catch(() => {})
    }
  } catch {
    // 静默处理，页面卸载时不应抛出异常
  }
}

/** 裁剪重发队列 */
const pruneResendQueue = (): number => {
  const MAX_SIZE = 100
  const count = requestQueue.length
  const overflow = Math.max(0, count - MAX_SIZE)
  if (overflow > 0) {
    console.log(`[SLSLogger] Discarded ${overflow} logs.`)
    requestQueue.splice(0, overflow)
  }
  return overflow
}

/** 重发失败的日志 */
const resendFailedLogs = async () => {
  if (resendStarted) return

  console.log(`[SLSLogger] Resending ${requestQueue.length} failed log(s).`)
  resendStarted = true
  let failures = 0

  for (let i = 0; i < requestQueue.length; i++) {
    i = Math.max(0, i - pruneResendQueue())
    try {
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 1000)

      // 逐条重发并按下标原地删除队列，必须串行
      // oxlint-disable-next-line no-await-in-loop
      await fetch(requestQueue[i], {
        method: "GET",
        mode: "cors",
        credentials: "omit",
        signal: controller.signal,
      })

      clearTimeout(timeoutId)
      requestQueue.splice(i--, 1)
    } catch {
      failures++
    }
  }

  if (failures > 0) {
    console.warn(`[SLSLogger] Some logs were not sent (${failures} failed).`)
  }
  resendStarted = false
}

/** 获取元素文本内容 */
const getElementText = (element: Element): string => {
  let text = ""

  if (element instanceof HTMLInputElement) {
    text = element.value || element.placeholder || ""
  } else if (element instanceof HTMLButtonElement) {
    text = element.textContent || ""
  } else if (element instanceof HTMLImageElement) {
    text = element.alt || element.title || ""
  } else if (element instanceof HTMLAnchorElement) {
    text = element.textContent || element.title || ""
  } else {
    text = element.textContent || ""
  }

  text = text.trim().replace(/\s+/g, " ")
  if (text.length > 100) {
    text = text.substring(0, 100) + "..."
  }
  return text
}

/** 获取元素选择器路径 */
const getElementSelector = (element: Element): string => {
  const path: string[] = []
  let current: Element | null = element

  while (current && current !== document.body) {
    let selector = current.tagName.toLowerCase()

    if (current.id) {
      selector += `#${current.id}`
    }

    if (current.className && typeof current.className === "string") {
      const classes = current.className
        .split(" ")
        .filter((c) => c.trim())
        .slice(0, 2)
      if (classes.length > 0) {
        selector += `.${classes.join(".")}`
      }
    }

    path.unshift(selector)
    current = current.parentElement

    if (path.length >= 5) break
  }

  return path.join(" > ")
}

/**
 * SLS 日志服务实例
 */
export const slsLogger = {
  /** 设置用户 ID */
  setUserId(id: string) {
    userId = id
  },

  /** 记录页面访问 */
  trackPage(path: string, pvKey: string, data: Record<string, unknown> = {}) {
    sendEventLog({ eventType: "page", path, pvKey, data })
  },

  /** 记录页面离开 */
  trackLeavePage(
    path: string,
    pvKey: string,
    data: Record<string, unknown> = {},
  ) {
    sendEventLog({ eventType: "leavePage", path, pvKey, data })
  },

  /** 记录页面离开（同步，用于页面卸载） */
  trackLeavePageSync(
    path: string,
    pvKey: string,
    data: Record<string, unknown> = {},
  ) {
    sendEventLogSync({ eventType: "leavePage", path, pvKey, data })
  },

  /** 记录用户行为 */
  trackAction(
    action: string,
    category: string,
    data: Record<string, unknown> = {},
    pvKey?: string,
  ) {
    const resolvedPvKey = resolvePvKey(pvKey, data)
    sendEventLog({
      eventType: "action",
      action,
      category,
      data: { ...data, pvKey: resolvedPvKey },
    })
  },

  /** 记录错误事件 */
  trackError(
    error: Error | string,
    context: Record<string, unknown> = {},
    pvKey?: string,
  ) {
    const resolvedPvKey = resolvePvKey(pvKey, context)
    const errorData =
      typeof error === "string" ? { message: error } : { ...error }
    sendEventLog({
      eventType: "error",
      data: { ...errorData, ...context, pvKey: resolvedPvKey },
    })
  },
}

/** 启用自动页面追踪 */
export const enableAutoPageTracking = () => {
  const trackPageChange = () => {
    const newPath = window.location.pathname + window.location.search
    if (newPath !== currentPath) {
      if (currentPath && currentPvKey && !hasTrackedLeave) {
        slsLogger.trackLeavePage(currentPath, currentPvKey)
        previousPath = currentPath
      }

      currentPath = newPath
      currentPvKey = generatePvKey()
      hasTrackedLeave = false

      slsLogger.trackPage(newPath, currentPvKey, {
        ua: navigator.userAgent,
      })
    }
  }

  window.addEventListener("popstate", trackPageChange)

  const originalPushState = history.pushState
  const originalReplaceState = history.replaceState

  history.pushState = function (...args) {
    originalPushState.apply(this, args)
    setTimeout(trackPageChange, 0)
  }

  history.replaceState = function (...args) {
    originalReplaceState.apply(this, args)
    setTimeout(trackPageChange, 0)
  }

  // 初始页面追踪
  trackPageChange()

  // 页面卸载时记录离开事件
  const handlePageUnload = (reason: string) => {
    if (currentPath && currentPvKey && !hasTrackedLeave) {
      hasTrackedLeave = true
      slsLogger.trackLeavePageSync(currentPath, currentPvKey, { reason })
    }
  }

  window.addEventListener("beforeunload", () => handlePageUnload("page_unload"))
}

/** 启用自动错误追踪 */
export const enableAutoErrorTracking = () => {
  window.addEventListener("unhandledrejection", (event) => {
    slsLogger.trackError(event.reason, { type: "unhandledrejection" })
  })

  window.addEventListener("error", (event) => {
    slsLogger.trackError(event.error || event.message, {
      type: "error",
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    })
  })
}

/** 启用自动点击追踪 */
export const enableAutoClickTracking = () => {
  document.addEventListener("click", (event) => {
    try {
      const target = event.target as Element
      if (!target) return

      const elementText = getElementText(target)
      const elementSelector = getElementSelector(target)

      slsLogger.trackAction("click", "auto", {
        elementText,
        elementSelector,
        elementTag: target.tagName.toLowerCase(),
        pagePath: window.location.pathname + window.location.search,
        pageTitle: document.title,
      })
    } catch {
      // 静默处理点击追踪错误
    }
  })
}
